import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from 'redux';
import ActionTypes from '../../../../constants/ActionTypes';
import MiddlewareEntities from '../../../../constants/MiddlewareEntities';
import { setLoader } from '../../ui/actions';
import { getSessionData } from '../../sessionData/actions';
import { createErrorNotification, getNotification } from '../../notification/actions';
import { apiRequest } from '../../api/actions';
import {
  processCampaignSubmitAction,
  setAcceptanceRequestEmailStatusAction,
  setAcceptanceRequestSmsStatusAction,
  setAcceptanceStatusAction,
  setCampaignOverviewDataAction,
  setConsentsFromResponseAction,
  setInitialCampaignConsentAction,
  setOverviewCmUserAction,
  setOverviewIsActiveContactForMsisdnAction,
  setOverviewOfferAdditionalData,
  setOverviewOptionNameAction,
  setOverviewSoftOfferPackageAction,
  setOverviewTermsAction,
} from './actions';
import { ISoftOfferPackage } from '../../../../entities/campaigns/types';
import SoftOfferPackageFactory from '../../../../factories/campaigns/SoftOfferPackageFactory';
import ApiUrls from '../../../../constants/ApiUrls';
import { HttpConstants } from '../../../../constants/HttpConstants';
import { CampaignConstants } from '../../../../constants/CampaignConstants';
import CampaignSubmitObjectFactory from '../../../../factories/campaigns/CampaignSubmitObjectFactory';
import _ from 'lodash';
import history from '../../../../utilites/history';
import Config from '../../../../config/config';
import { setSoftElectroSubmitMessageAction, setSoftElectroSubmitStatusAction } from '../softElectro/actions';
import CheckoutSteps from '../../../../constants/CheckoutSteps';
import { setActiveStoreId, setStoresMapVisibilityAction } from '../../storesMap/actions';
import Segments from '../../../../constants/Segments';

export const getCampaignOverviewDataMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);
    switch (action.type) {
    case ActionTypes.CAMPAIGN.FETCH:
      next(
        apiRequest(
          null,
          HttpConstants.methods.GET,
          `${ApiUrls.CAMPAIGNS}/overview/`,
          {
            lang: action.params.lang,
            section: action.params.section,
            offerId: action.params.offerId,
            optionId: action.params.optionId,
            cnlId: action.params.cnlId,
            tpId: action.params.tpId,
          },
          null,
          MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA,
          {}
        )
      );
      next(setLoader(true, MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA));
      break;
    case `${MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA} ${ActionTypes.API_SUCCESS}`:
      next(setCampaignOverviewDataAction(action.payload.data));
      next(getSessionData(action.payload.data.session));
      next(getNotification(action.payload.data.session.flashMessages, MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA));
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA));
      break;
    case `${MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA} ${ActionTypes.API_ERROR}`:
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA));
      next(createErrorNotification(action.payload.data, MiddlewareEntities.CAMPAIGN.OVERVIEW_DATA));
      break;
    }
  };

export const createCampaignOverviewDataMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    if (action.type.includes(ActionTypes.CAMPAIGN.OVERVIEW.CREATE)) {
      const tpOffer: ISoftOfferPackage = SoftOfferPackageFactory.create(action.payload.campaignData.tp);
      next(setOverviewSoftOfferPackageAction(tpOffer));
      next(setOverviewOptionNameAction(action.payload.campaignData.optionName));
      next(
        setOverviewOfferAdditionalData(
          action.payload.campaignData.offerId,
          action.payload.campaignData.optionId,
          action.payload.campaignData.cnlId,
          action.payload.campaignData.contractDuration,
          action.payload.campaignData.p2p,
          action.payload.campaignData.p2pDj,
          action.payload.campaignData.upsell,
          action.payload.campaignData.stopNonResident,
        )
      );
      const termsText = _.replace(action.payload.terms, '<MFDISCOUNT>', String(tpOffer.getFinalPrice()));
      next(setOverviewTermsAction(termsText));
      next(setOverviewIsActiveContactForMsisdnAction(action.payload.isActiveContactForMsisdn));
      next(setOverviewCmUserAction(action.payload.cmUser));
      next(setInitialCampaignConsentAction(action.payload));
      next(setAcceptanceStatusAction(action.payload.cssConfirmationStatus));
    }
  };

export const campaignConsentMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    if (action.type.includes(ActionTypes.CAMPAIGN.CONSENT.CREATE)) {
      next(setConsentsFromResponseAction(action.payload.givenConsents));
    }
  };

export const campaignAcceptanceMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);
    switch (action.type) {
    case ActionTypes.CAMPAIGN.ACCEPTANCE.SEND_REQUEST:
      next(
        apiRequest(
          null,
          HttpConstants.methods.POST,
          `${ApiUrls.CAMPAIGNS}/${action.params.action}-acceptance/`,
          null,
          action.params,
          MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST,
          {}
        )
      );
      next(setLoader(true, MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST));
      break;
    case `${MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST} ${ActionTypes.API_SUCCESS}`:
      // set successfully send acceptance request by type
      if (action.payload.data.action === CampaignConstants.acceptance.SMS) {
        next(setAcceptanceRequestSmsStatusAction(true));
      }
      if (action.payload.data.action === CampaignConstants.acceptance.EMAIL) {
        next(setAcceptanceRequestEmailStatusAction(true));
      }
      next(getSessionData(action.payload.data.session));
      next(getNotification(action.payload.data.session.flashMessages, MiddlewareEntities.CAMPAIGN.ACCEPTANCE.ALL));
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.ACCEPTANCE.ALL));
      break;
    case `${MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST} ${ActionTypes.API_ERROR}`:
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST));
      next(
        createErrorNotification(action.payload.data, MiddlewareEntities.CAMPAIGN.ACCEPTANCE.SEND_ACCEPTANCE_REQUEST)
      );
      break;
    }
  };

export const cssStatusMiddleware: Middleware = (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
  next(action);
  switch (action.type) {
  case ActionTypes.CAMPAIGN.OVERVIEW.CHECK_CSS_STATUS:
    next(
      apiRequest(
        null,
        HttpConstants.methods.GET,
        `${ApiUrls.CAMPAIGNS}/confirmation-status?offerId=${action.params.offerId}&optionId=${action.params.optionId}&tpId=${action.params.tpId}`,
        null,
        null,
        MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS,
        {}
      )
    );
    next(setLoader(true, MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS));
    break;
  case `${MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS} ${ActionTypes.API_SUCCESS}`:
    next(setAcceptanceStatusAction(action.payload.data.cssConfirmationStatus));
    next(getSessionData(action.payload.data.session));
    next(getNotification(action.payload.data.session.flashMessages, MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS));
    next(setLoader(false, MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS));
    break;
  case `${MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS} ${ActionTypes.API_ERROR}`:
    next(setLoader(false, MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS));
    next(createErrorNotification(action.payload.data, MiddlewareEntities.CAMPAIGN.OVERVIEW_CSS));
    break;
  }
};

export const campaignSubmitMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);
    switch (action.type) {
    case ActionTypes.CAMPAIGN.OVERVIEW.SUBMIT:
      const submitObject = CampaignSubmitObjectFactory.create(store.getState());
      submitObject.data.lsId = action.payload;
      next(
        apiRequest(
          null,
          HttpConstants.methods.POST,
          `${ApiUrls.CAMPAIGNS}/submit/`,
          null,
          submitObject,
          MiddlewareEntities.CAMPAIGN.SUBMIT,
          {}
        )
      );
      next(setLoader(true, MiddlewareEntities.CAMPAIGN.SUBMIT));
      break;
    case `${MiddlewareEntities.CAMPAIGN.SUBMIT} ${ActionTypes.API_SUCCESS}`:
      next(processCampaignSubmitAction(action.payload.data));
      next(getSessionData(action.payload.data.session));
      next(getNotification(action.payload.data.session.flashMessages, MiddlewareEntities.CAMPAIGN.SUBMIT));
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.SUBMIT));
      break;
    case `${MiddlewareEntities.CAMPAIGN.SUBMIT} ${ActionTypes.API_ERROR}`:
      next(setLoader(false, MiddlewareEntities.CAMPAIGN.SUBMIT));
      next(createErrorNotification(action.payload.data, MiddlewareEntities.CAMPAIGN.SUBMIT));
      break;
    }
  };

export const campaignSubmitProcessMiddleware: Middleware =
  (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    next(action);

    if (action.type.includes(ActionTypes.CAMPAIGN.OVERVIEW.SUBMIT_PROCESS)) {
      // Transaction flow
      if (_.has(action.payload.data, 'transaction')) {
        if (sessionStorage.getItem('campaignSuccessType')) {
          // this key is only for tpUpsell and soft renewal types of campaign
          let link = `${Config.domain}${Config.prefixUrl}/sr/${Segments.sr.PRIVATE_SEGMENT}/campaigns/success/${action.payload.data.transaction}`;
          if (_.has(action.payload.data, 'promotions')) {
            link = link.concat(`?v=${action.payload.data.promotions.cj2Amount}`);
          }
          window.location.replace(link);
          return;
        }
        window.location.replace(
          `${Config.domain}${Config.prefixUrl}/sr/${Segments.sr.PRIVATE_SEGMENT}/${CheckoutSteps.sr.transactionOverview}/${action.payload.data.transaction}`
        );
      }

      // P2P flow
      if (_.has(action.payload, 'referenceId')) {
        window.location.replace(Config.cartOverviewNewUrl);
      }

      // Electro flow
      if (_.has(action.payload, 'sendSmsForSoftRenewalResponse')) {
        next(setSoftElectroSubmitStatusAction(action.payload.sendSmsForSoftRenewalResponse.status));
        next(
          setSoftElectroSubmitMessageAction(
            action.payload.sendSmsForSoftRenewalResponse.message
              ? action.payload.sendSmsForSoftRenewalResponse.message
              : action.payload.sendSmsForSoftRenewalResponse.sendSmsForSoftRenewalResponse
          )
        );
        history.push(`${Config.prefixUrl}/sr/campaigns/sms-electro-transaction`);
      }
    }

    if (action.type === ActionTypes.CAMPAIGN.OVERVIEW.SET_SELECTED_STORE_ID) {
      next(setActiveStoreId(action.payload));
      next(setStoresMapVisibilityAction(false));
    }
  };

export const campaignOverviewMiddleware = [
  getCampaignOverviewDataMiddleware,
  createCampaignOverviewDataMiddleware,
  campaignConsentMiddleware,
  campaignAcceptanceMiddleware,
  cssStatusMiddleware,
  campaignSubmitMiddleware,
  campaignSubmitProcessMiddleware,
];
